import { useEffect, useMemo, useState } from "react";
import { api, currency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const empty = { title: "", variantsText: "", price: "", costExcl: "", costIncl: "", vat_applicable: true };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        p.title.toLowerCase().includes(q) ||
        (p.variants || []).some((v) => v.toLowerCase().includes(q))
    );
  }, [products, query]);

  const load = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Product title is required");
    setSaving(true);
    try {
      const variants = form.variantsText
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      const payload = {
        title: form.title,
        variants,
        price: parseFloat(form.price) || 0,
        cost_price_excl: parseFloat(form.costExcl) || 0,
        cost_price_incl: parseFloat(form.costIncl) || 0,
        vat_applicable: form.vat_applicable,
      };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setForm(empty);
      setEditingId(null);
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      variantsText: (p.variants || []).join(", "),
      price: p.price ?? "",
      costExcl: p.cost_price_excl ?? "",
      costIncl: p.cost_price_incl ?? "",
      vat_applicable: p.vat_applicable,
    });
    setOpen(true);
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const remove = (id, title) => setDeleteTarget({ id, title });

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success("Product deleted");
      setDeleteTarget(null);
      load();
    } catch { toast.error("Failed to delete product"); }
    finally { setDeleting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#032352]">Products</h1>
          <p className="text-[#5B5C5F] mt-1">Manage your product catalogue.</p>
        </div>
        <Button data-testid="add-product-button" onClick={openNew} className="bg-[#032352] hover:bg-[#04347A]">
          <Plus className="w-4 h-4 mr-2" /> New Product
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#032352]">
                {editingId ? "Edit Product" : "New Product"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Product Title</Label>
                <Input
                  data-testid="product-title-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="M10 Hex Bolt"
                />
              </div>
              <div>
                <Label>Variant Options (comma separated)</Label>
                <Input
                  data-testid="product-variants-input"
                  value={form.variantsText}
                  onChange={(e) => setForm({ ...form, variantsText: e.target.value })}
                  placeholder="Zinc, Stainless, Black"
                />
              </div>
              <div>
                <Label>Sale Price (£, excl VAT)</Label>
                <Input
                  data-testid="product-price-input"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Cost Price (excl VAT)</Label>
                  <Input
                    data-testid="product-cost-excl-input"
                    type="number"
                    step="0.01"
                    value={form.costExcl}
                    onChange={(e) => {
                      const excl = e.target.value;
                      const incl = excl === "" ? "" : (parseFloat(excl) * 1.2).toFixed(2);
                      setForm({ ...form, costExcl: excl, costIncl: incl });
                    }}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Cost Price (incl VAT)</Label>
                  <Input
                    data-testid="product-cost-incl-input"
                    type="number"
                    step="0.01"
                    value={form.costIncl}
                    onChange={(e) => setForm({ ...form, costIncl: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <div>
                  <Label>VAT Applicable</Label>
                  <p className="text-xs text-[#5B5C5F]">Charge 20% VAT on this product</p>
                </div>
                <Switch
                  data-testid="product-vat-switch"
                  checked={form.vat_applicable}
                  onCheckedChange={(v) => setForm({ ...form, vat_applicable: v })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="save-product-button"
                onClick={save}
                disabled={saving}
                className="bg-[#032352] hover:bg-[#04347A]"
              >
                Save Product
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative w-full max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5C5F]" />
        <Input
          data-testid="products-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products..."
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead>Title</TableHead>
              <TableHead>Variants</TableHead>
              <TableHead>VAT</TableHead>
              <TableHead className="text-right">Cost (excl)</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-[#5B5C5F] py-10">
                  No products found.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell className="font-medium text-[#032352]">{p.title}</TableCell>
                <TableCell className="text-sm">
                  <div className="flex flex-wrap gap-1">
                    {p.variants.map((v) => (
                      <Badge key={v} variant="secondary" className="font-normal">
                        {v}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {p.vat_applicable ? (
                    <Badge className="bg-[#032352]">20%</Badge>
                  ) : (
                    <Badge variant="outline">No VAT</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono-num text-[#5B5C5F]">
                  {currency(p.cost_price_excl || 0)}
                </TableCell>
                <TableCell className="text-right font-mono-num text-[#032352]">
                  {currency(p.price)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3 justify-end">
                    <button
                      data-testid={`edit-product-${p.id}`}
                      onClick={() => openEdit(p)}
                      className="text-slate-400 hover:text-[#032352] transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-product-${p.id}`}
                      onClick={() => remove(p.id, p.title)}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title={`Delete "${deleteTarget?.title}"?`}
        description="This will permanently delete the product. This cannot be undone."
      />
    </div>
  );
}
